import React, { useMemo } from "react";
import { Trophy, BarChart3 } from "lucide-react";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { formatCurrency } from "@/lib/commission";
import { TIER_IDS, isTierId, type TierId } from "@/lib/tiers";
import { InfluencerTierBadge } from "../tiers/InfluencerTierBadge";
import {
  currentMonthKey,
  type InfluencerRecord,
  type MonthlyTotals,
} from "./hooks/useInfluencersData";

const PT_MONTHS_SHORT: Record<string, string> = {
  "01": "Jan", "02": "Fev", "03": "Mar", "04": "Abr", "05": "Mai", "06": "Jun",
  "07": "Jul", "08": "Ago", "09": "Set", "10": "Out", "11": "Nov", "12": "Dez",
};

function monthLabel(monthYear: string): string {
  const [year, month] = monthYear.split("-");
  return `${PT_MONTHS_SHORT[month] || month}/${year.slice(2)}`;
}

function compactCurrency(v: number): string {
  if (v >= 1000) return `R$ ${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k`;
  return `R$ ${Math.round(v)}`;
}

function initials(name: string | null): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || "?";
}

const ChartCard: React.FC<{ title: string; subtitle?: string; className?: string; children: React.ReactNode }> = ({
  title,
  subtitle,
  className = "",
  children,
}) => (
  <div className={`rounded-xl border border-white/10 bg-gradient-to-b from-white/[0.05] to-white/[0.02] p-4 ${className}`}>
    <div className="mb-3">
      <h3 className="text-sm font-semibold">{title}</h3>
      {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
    </div>
    {children}
  </div>
);

const TIER_LABELS: Record<TierId | "none", string> = {
  starter: "Starter",
  oficial: "Oficial",
  elite: "Elite",
  top5: "Top 5 do Mês",
  none: "Sem categoria",
};

interface AnalyticsChartsProps {
  rows: InfluencerRecord[];
  monthlyTotals: MonthlyTotals[];
  loading: boolean;
}

/**
 * Decision-making layer above the detailed analytics table: sales over time,
 * this month's top performers, and sales by tier. Presentation only — all
 * numbers derive from the same data as the rest of the dashboard.
 */
export const AnalyticsCharts: React.FC<AnalyticsChartsProps> = ({ rows, monthlyTotals, loading }) => {
  const monthNow = currentMonthKey();

  const timeline = useMemo(
    () =>
      monthlyTotals.slice(-12).map((m) => ({
        label: monthLabel(m.month),
        vendas: Math.round(m.sales * 100) / 100,
      })),
    [monthlyTotals]
  );

  const topPerformers = useMemo(
    () =>
      rows
        .map((r) => ({ record: r, sales: r.months[monthNow]?.total_sales || 0 }))
        .filter((e) => e.sales > 0)
        .sort((a, b) => b.sales - a.sales)
        .slice(0, 5),
    [rows, monthNow]
  );

  const salesByTier = useMemo(() => {
    const totals = new Map<TierId | "none", { sales: number; count: number }>();
    for (const key of [...TIER_IDS, "none" as const]) totals.set(key, { sales: 0, count: 0 });
    for (const r of rows) {
      const key = isTierId(r.tier_id) ? r.tier_id : "none";
      const entry = totals.get(key)!;
      entry.sales += r.months[monthNow]?.total_sales || 0;
      entry.count++;
    }
    const list = Array.from(totals.entries()).map(([tier, { sales, count }]) => ({ tier, sales, count }));
    const max = Math.max(...list.map((e) => e.sales), 1);
    return { list, max };
  }, [rows, monthNow]);

  if (loading) {
    return (
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="skeleton-shimmer h-56 lg:col-span-2 rounded-xl" />
        <div className="skeleton-shimmer h-56 rounded-xl" />
        <div className="skeleton-shimmer h-40 lg:col-span-3 rounded-xl" />
      </div>
    );
  }

  const hasAnySales = timeline.some((t) => t.vendas > 0);

  return (
    <div className="grid gap-4 lg:grid-cols-3">
      {/* Sales over time */}
      <ChartCard title="Vendas ao longo do tempo" subtitle="Total mensal de todas as influenciadoras" className="lg:col-span-2">
        {!hasAnySales ? (
          <div className="flex h-52 flex-col items-center justify-center text-center">
            <BarChart3 className="h-7 w-7 text-muted-foreground/40 mb-2" />
            <p className="text-sm text-muted-foreground">Sem vendas registradas ainda.</p>
          </div>
        ) : (
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={timeline} margin={{ top: 6, right: 8, bottom: 0, left: 4 }}>
                <defs>
                  <linearGradient id="analytics-sales" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--primary)" stopOpacity={0.16} />
                    <stop offset="100%" stopColor="var(--primary)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.06)" strokeWidth={1} vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fill: "rgba(255,255,255,0.45)", fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: "rgba(255,255,255,0.45)", fontSize: 11 }}
                  tickFormatter={compactCurrency}
                  axisLine={false}
                  tickLine={false}
                  width={56}
                />
                <Tooltip
                  cursor={{ stroke: "rgba(255,255,255,0.15)", strokeWidth: 1 }}
                  content={({ active, payload, label }) =>
                    active && payload?.length ? (
                      <div className="rounded-md border border-white/10 bg-background px-2.5 py-1.5 text-xs shadow-lg">
                        <p className="text-muted-foreground">{label}</p>
                        <p className="font-semibold tabular-nums">{formatCurrency(Number(payload[0].value))}</p>
                      </div>
                    ) : null
                  }
                />
                <Area
                  type="monotone"
                  dataKey="vendas"
                  stroke="var(--primary)"
                  strokeWidth={2}
                  fill="url(#analytics-sales)"
                  dot={false}
                  activeDot={{ r: 4, strokeWidth: 2, stroke: "var(--background)" }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </ChartCard>

      {/* Top performers this month */}
      <ChartCard title="Top do mês" subtitle="Maiores vendas neste mês">
        {topPerformers.length === 0 ? (
          <div className="flex h-52 flex-col items-center justify-center text-center">
            <Trophy className="h-7 w-7 text-muted-foreground/40 mb-2" />
            <p className="text-sm text-muted-foreground">Nenhuma venda neste mês ainda.</p>
          </div>
        ) : (
          <div className="space-y-2.5">
            {topPerformers.map(({ record, sales }, idx) => {
              const share = (sales / topPerformers[0].sales) * 100;
              return (
                <div key={record.id} className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span
                      className={`w-4 text-center text-xs font-semibold tabular-nums ${
                        idx === 0 ? "text-amber-300" : "text-muted-foreground"
                      }`}
                    >
                      {idx + 1}
                    </span>
                    <Avatar className="h-6 w-6 border border-white/10 shrink-0">
                      {record.avatar_url && <AvatarImage src={record.avatar_url} alt="" />}
                      <AvatarFallback className="bg-white/10 text-[9px] font-semibold">
                        {initials(record.name)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="flex-1 truncate text-sm font-medium">{record.name || record.coupon}</span>
                    <span className="text-sm font-semibold tabular-nums">{formatCurrency(sales)}</span>
                  </div>
                  <div className="ml-6 h-1 rounded-full bg-white/5 overflow-hidden">
                    <div
                      className={`h-full rounded-full ${idx === 0 ? "bg-amber-400/70" : "bg-primary/50"}`}
                      style={{ width: `${share}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </ChartCard>

      {/* Sales by tier (current month) */}
      <ChartCard
        title="Vendas por categoria"
        subtitle="Neste mês, somadas por categoria atual"
        className="lg:col-span-3"
      >
        <div className="space-y-2.5">
          {salesByTier.list.map(({ tier, sales, count }) => (
            <div key={tier} className="flex items-center gap-3">
              <span className="w-36 shrink-0">
                {tier === "none" ? (
                  <span className="text-xs text-muted-foreground">{TIER_LABELS.none}</span>
                ) : (
                  <InfluencerTierBadge tierId={tier} />
                )}
              </span>
              <div className="flex-1 h-4 rounded-r bg-white/[0.04] overflow-hidden">
                <div
                  className="h-full rounded-r bg-primary/70"
                  style={{ width: `${Math.max((sales / salesByTier.max) * 100, sales > 0 ? 1.5 : 0)}%` }}
                />
              </div>
              <span className="w-24 text-right text-sm font-medium tabular-nums shrink-0">
                {formatCurrency(sales)}
              </span>
              <span className="w-20 text-right text-xs text-muted-foreground tabular-nums shrink-0">
                {count} {count === 1 ? "pessoa" : "pessoas"}
              </span>
            </div>
          ))}
        </div>
      </ChartCard>
    </div>
  );
};

export default AnalyticsCharts;
